// 受注試算の人件費「所要時間ベース」— 純ロジック（Supabase・DOM・祝日データ非依存）。
// 2026-08-28 オーナー決定（料率指定）。正本: secretary-ナギ/notes/2026-08-28-decisions.md
// テスト: test_labor_time_quote.mjs
//
// 式: 拘束時間 ＝ Google走行時間 × トラック係数 ＋ 積卸バッファ × 地点数
//     人件費 ＝ (8hまで×時給 ＋ 8h超×時給×1.25) × (1 ＋ 日祝着0.20 ＋ 深夜0.20)
//
// 割増の重ね方（**この1箇所に集約**。設定化しない＝オーナーが変えたい時はここを変える）:
//  - 時間外×1.25 は「8時間を超えた時間部分」だけに掛かる（8時間までは基本時給）
//  - 日祝「着」+20% と 深夜の積みor着け +20% は人件費全体に掛かり、両方該当なら**加算**（+40%）
// 泊まりがけ（営業日2日以上）も**1運行として扱い、8h閾値を日毎に分割しない**。
//  理由: 拘束時間は Google走行時間由来＋積卸バッファのみで、宿泊・休息時間を算入しない＝
//  過大請求側に膨らまない。日別配分は根拠になるデータ（各日の走行配分）が無く複雑になるだけ。
export const LABOR_TIME_DEFAULTS = {
  hourlyMedium: 2200,  // 中型 時給（円/h・オーナー指定）
  hourlyLarge: 2400,   // 大型 時給（円/h・オーナー指定）
  driveFactor: 1.2,    // トラック係数: Google の乗用車想定に対し法定速度・休憩で2割増し（実装判断・設定で調整可）
  stopBufferMin: 30,   // 積卸バッファ: 1地点あたり待機＋作業30分（2地点で計1h。実装判断・設定で調整可）
}
export const OVERTIME_START_H = 8    // 8時間まで基本時給
export const OVERTIME_MULT = 1.25    // 8時間超の部分は×1.25
export const HOLIDAY_ARRIVE_PCT = 0.2 // 日祝「着」+20%（着け日が日曜・祝日）
export const NIGHT_PCT = 0.2         // 深夜の積み or 着け +20%（22:00〜翌5:00）

const posNum = (v, def) => { const n = Number(v); return isFinite(n) && n > 0 ? n : def }
const nonNegNum = (v, def) => { const n = Number(v); return (v != null && v !== '' && isFinite(n) && n >= 0) ? n : def }

// settings/labor の新フィールド → 正規化。未設定・不正値は既定へ倒す（settings/display の先例踏襲）。
// バッファのみ 0 を有効値とする（バッファ無し運用を許す）。
export function normLaborTime(doc) {
  const d = doc || {}
  return {
    hourlyMedium: posNum(d.hourlyMedium, LABOR_TIME_DEFAULTS.hourlyMedium),
    hourlyLarge: posNum(d.hourlyLarge, LABOR_TIME_DEFAULTS.hourlyLarge),
    driveFactor: posNum(d.driveFactor, LABOR_TIME_DEFAULTS.driveFactor),
    stopBufferMin: nonNegNum(d.stopBufferMin, LABOR_TIME_DEFAULTS.stopBufferMin),
  }
}

// 'HH:MM' が深夜（22:00〜翌5:00）に入るか。22:00 は深夜・5:00 は昼間（オーナー表記「昼間5-22時」の外）。
// 未入力・時刻として読めない値は false（＝割増を適用しない。決定の正本どおり）。
export function isNightTime(hhmm) {
  const m = String(hhmm == null ? '' : hhmm).trim().match(/^(\d{1,2}):(\d{2})/)
  if (!m) return false
  const h = Number(m[1]), mi = Number(m[2])
  if (h > 23 || mi > 59) return false
  const mins = h * 60 + mi
  return mins >= 22 * 60 || mins < 5 * 60
}

// 拘束時間（h・小数1位まるめ）。所要時間が無ければ null。地点数不明・2未満は2地点（積1卸1）扱い。
export function boundHours(durationMin, stopCount, cfg) {
  const dur = Number(durationMin)
  if (!(dur > 0)) return null
  const c = normLaborTime(cfg)
  const stops = Number(stopCount) >= 2 ? Math.floor(Number(stopCount)) : 2
  return Math.round((dur / 60 * c.driveFactor + c.stopBufferMin * stops / 60) * 10) / 10
}

// 所要時間ベースの人件費。durationMin が無ければ null（呼び側が従来の運賃×%へフォールバック）。
// cls: 'large'|'medium'（quoteOwnTrip の既存 vClass 判定を渡す）。
export function timeLaborCost({ durationMin, stopCount, cls, cfg, holidayArrive, night }) {
  const hours = boundHours(durationMin, stopCount, cfg)
  if (hours == null) return null
  const c = normLaborTime(cfg)
  const hourly = cls === 'large' ? c.hourlyLarge : c.hourlyMedium
  const baseH = Math.min(hours, OVERTIME_START_H)
  const overH = Math.round(Math.max(hours - OVERTIME_START_H, 0) * 10) / 10
  const baseYen = Math.round(baseH * hourly)
  const overYen = Math.round(overH * hourly * OVERTIME_MULT)
  const surchargePct = (holidayArrive ? HOLIDAY_ARRIVE_PCT : 0) + (night ? NIGHT_PCT : 0)
  const labor = Math.round((baseYen + overYen) * (1 + surchargePct))
  return { hours, hourly, baseH, overH, baseYen, overYen, surchargePct, holidayArrive: !!holidayArrive, night: !!night, labor }
}
